import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { useJobTracker } from '../../providers/JobTrackerProvider';
import {
  getClientEmailHealth,
  runEmailHealthCheck,
  getSpamHeroSettings,
  fixEnhancedFiltering,
  SpamHeroCheckResult,
} from '../../services/api';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  MinusCircle,
  RefreshCw,
  Loader2,
  ShieldCheck,
  Mail,
  Filter,
  Network,
  Clock,
  ChevronDown,
  ChevronRight,
  Wrench,
  Globe,
  Key,
  Clipboard,
  ClipboardCheck,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

interface ClientEmailHealthProps {
  clientId: string;
  clientName: string;
}

function OverallBanner({ status, checks, usesSpamHero }: { status: string; checks?: SpamHeroCheckResult['checks']; usesSpamHero?: boolean }) {
  // Build context-aware message based on what's actually failing
  const getIssueDescription = () => {
    const issues: string[] = [];
    if (checks) {
      if (usesSpamHero) {
        if (checks.inboundConnector?.status === 'fail') issues.push('SpamHero connector misconfigured');
        if (checks.enhancedFiltering?.status === 'fail') issues.push('Enhanced Filtering not enabled');
        if (checks.ipMatch?.status === 'fail') issues.push('connector IP mismatch');
      }
      if (checks.spf?.status === 'fail') issues.push('missing SPF record');
      if (checks.dkim?.status === 'fail' || checks.dkim?.status === 'warning') issues.push('DKIM not configured');
      if (checks.dmarc?.status === 'fail') issues.push('missing DMARC record');
      if (checks.dmarc?.status === 'warning') issues.push('DMARC policy set to none');
    }
    return issues.length > 0 ? issues.join(', ') : 'review details below';
  };

  if (status === 'healthy') {
    return (
      <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-4 flex items-center gap-3">
        <CheckCircle className="h-6 w-6 text-emerald-500 shrink-0" />
        <div>
          <div className="font-semibold text-emerald-600 dark:text-emerald-400">Email health is good</div>
          <div className="text-sm text-emerald-600/80 dark:text-emerald-400/80">
            {usesSpamHero ? 'SpamHero connector and email authentication are properly configured' : 'Email authentication (SPF, DKIM, DMARC) is properly configured'}
          </div>
        </div>
      </div>
    );
  }
  if (status === 'critical') {
    return (
      <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4 flex items-center gap-3">
        <XCircle className="h-6 w-6 text-red-500 shrink-0" />
        <div>
          <div className="font-semibold text-red-600 dark:text-red-400">Critical configuration issue</div>
          <div className="text-sm text-red-600/80 dark:text-red-400/80">{getIssueDescription()}</div>
        </div>
      </div>
    );
  }
  if (status === 'issues') {
    return (
      <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-4 flex items-center gap-3">
        <AlertTriangle className="h-6 w-6 text-amber-500 shrink-0" />
        <div>
          <div className="font-semibold text-amber-600 dark:text-amber-400">Issues detected</div>
          <div className="text-sm text-amber-600/80 dark:text-amber-400/80">{getIssueDescription()}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-muted border border-border p-4 flex items-center gap-3">
      <MinusCircle className="h-6 w-6 text-muted-foreground shrink-0" />
      <div>
        <div className="font-semibold text-muted-foreground">Not applicable</div>
        <div className="text-sm text-muted-foreground">This client does not use SpamHero for email filtering</div>
      </div>
    </div>
  );
}

function getRemediationGuides(knownIps: string[]): Record<string, string[]> {
  const ipList = knownIps.length > 0
    ? knownIps.join(', ')
    : '(configure IPs in Email Health Settings first)';
  return {
    mxRouting: [
      'Log in to your DNS provider (GoDaddy, Cloudflare, etc.)',
      'Navigate to DNS management for the client\'s domain',
      'Update MX records to point to SpamHero (mxthunder.com)',
      'Remove any MX records pointing directly to Microsoft 365',
      'Wait 15-60 minutes for DNS propagation',
      'Re-run this check to verify the change',
    ],
    inboundConnector: [
      'Go to Exchange Admin Center (admin.exchange.microsoft.com)',
      'Navigate to Mail flow > Connectors > Add a connector',
      'Set "Connection from" to "Partner organization", "Connection to" to "Office 365"',
      'Name it "SpamHero Inbound"',
      `Under "How do you want to identify the partner organization?", select "By verifying that messages are sent from these IP addresses" and add: ${ipList}`,
      'On "Security restrictions", leave as None (no TLS requirement)',
      'Set status to "Turn it on after saving" and save',
      'Re-run this check to verify',
    ],
    enhancedFiltering: [
      'Go to Microsoft Defender (security.microsoft.com)',
      'Navigate to Email & collaboration > Policies & rules > Threat policies > Enhanced Filtering for Connectors',
      'Check the box next to the SpamHero inbound connector to open the settings panel',
      `Select "Skip these IP addresses that are associated with the connector" and enter the SpamHero IPs: ${ipList}`,
      'Under "Apply to", select "Apply to entire organization"',
      'Click Save',
      'Re-run this check to verify',
    ],
    ipMatch: [
      'Go to Exchange Admin Center (admin.exchange.microsoft.com)',
      'Navigate to Mail flow > Connectors',
      'Edit the SpamHero inbound connector',
      `Update the sender IP addresses to: ${ipList}`,
      'Save the connector changes',
      'If SpamHero changed their IPs, also update the known-good IPs in Email Health Settings',
      'Re-run this check to verify',
    ],
  };
}

function copyToClipboard(text: string): Promise<boolean> {
  // Always use textarea fallback — it's the most reliable across all contexts
  // navigator.clipboard.writeText fails silently in iframes, dialogs with stopPropagation, etc.
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    // Must be visible enough for selection to work, but off-screen
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length); // iOS needs this
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return Promise.resolve(ok);
  } catch {
    return Promise.resolve(false);
  }
}

/** Parse old cached details strings into structured DNS records */
function parseLegacyDnsRecords(checkKey: string, details: string): { type: string; host: string; value: string; description?: string }[] {
  const records: { type: string; host: string; value: string; description?: string }[] = [];

  if (checkKey === 'dkim') {
    // Old format: "Create these CNAME records in DNS...\nselector1._domainkey.domain → target\nselector2._domainkey.domain → target\nThen enable..."
    const lines = details.split('\n');
    for (const line of lines) {
      const match = line.match(/^(selector(\d)\._domainkey)(?:\.\S+)?\s*→\s*(.+)$/);
      if (match) {
        // Strip the domain — DNS providers only need the subdomain part (selector1._domainkey)
        records.push({ type: 'CNAME', host: match[1], value: match[3].trim(), description: `DKIM Selector ${match[2]}` });
      }
    }
  } else if (checkKey === 'dmarc') {
    // Old format: "Create this TXT record in DNS:\nHost: _dmarc.domain\nValue: v=DMARC1; ...\nStart with p=none..."
    const valueMatch = details.match(/Value:\s*(.+?)(?:\n|$)/);
    if (valueMatch) {
      records.push({ type: 'TXT', host: '_dmarc', value: valueMatch[1].trim(), description: 'DMARC Policy Record' });
    }
  } else if (checkKey === 'spf') {
    // Old format: "Create TXT record:\nv=spf1 ..."
    const lines = details.split('\n');
    if (lines.length >= 2 && lines[1]?.startsWith('v=spf1')) {
      records.push({ type: 'TXT', host: '@', value: lines[1].trim(), description: 'SPF Record' });
    }
  }

  return records;
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [fieldCopied, setFieldCopied] = useState(false);
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard(value).then(() => {
      setFieldCopied(true);
      setTimeout(() => setFieldCopied(false), 2000);
    });
  };
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 bg-muted rounded px-3 py-2 text-sm font-mono text-foreground break-all select-all">{value}</code>
        <button onClick={copy} className="shrink-0 p-2 rounded-md border border-border hover:bg-accent transition-colors" title="Copy">
          {fieldCopied ? <ClipboardCheck className="h-4 w-4 text-emerald-500" /> : <Clipboard className="h-4 w-4 text-muted-foreground" />}
        </button>
      </div>
    </div>
  );
}

function DnsRecordRow({ rec }: { rec: { type: string; host: string; value: string; description?: string } }) {
  return (
    <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">{rec.description || `${rec.type} Record`}</span>
        <span className="text-xs bg-primary-600/10 text-primary-600 dark:text-primary-400 px-2 py-0.5 rounded font-medium">{rec.type}</span>
      </div>
      <CopyField label="Host / Name" value={rec.host} />
      <CopyField label="Value / Points To" value={rec.value} />
    </div>
  );
}

function CheckCard({ checkKey, title, icon: Icon, detail, knownIps, onFix, fixState }: {
  checkKey: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  detail: SpamHeroCheckResult['checks']['mxRouting'];
  knownIps: string[];
  onFix?: () => void;
  fixState?: { loading: boolean; success: string | null; error: string | null };
}) {
  const [showRemediation, setShowRemediation] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const remediation = getRemediationGuides(knownIps)[checkKey];

  const statusConfig = {
    pass: { border: 'border-emerald-500/30', icon: CheckCircle, iconClass: 'text-emerald-500', bg: 'bg-emerald-500/5' },
    fail: { border: 'border-red-500/30', icon: XCircle, iconClass: 'text-red-500', bg: 'bg-red-500/5' },
    warning: { border: 'border-amber-500/30', icon: AlertTriangle, iconClass: 'text-amber-500', bg: 'bg-amber-500/5' },
    unknown: { border: 'border-border', icon: MinusCircle, iconClass: 'text-muted-foreground', bg: '' },
    'n/a': { border: 'border-border', icon: MinusCircle, iconClass: 'text-muted-foreground', bg: '' },
  };

  const config = statusConfig[detail.status];
  const StatusIcon = config.icon;

  return (
    <div className={`rounded-xl border ${config.border} ${config.bg} bg-card p-5 space-y-3 cursor-pointer hover:ring-1 hover:ring-primary-500/30 transition-all`} onClick={() => setShowModal(true)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-muted-foreground" />
          <h4 className="font-semibold text-foreground">{title}</h4>
        </div>
        <StatusIcon className={`h-5 w-5 ${config.iconClass}`} />
      </div>
      <p className="text-sm text-foreground">{detail.message}</p>
      {detail.spfLookupCount !== undefined && (
        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
          detail.spfLookupCount > 10 ? 'bg-red-500/15 text-red-600 dark:text-red-400' :
          detail.spfLookupCount === 10 ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' :
          detail.spfLookupCount >= 8 ? 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400' :
          'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
        }`}>
          DNS Lookups: {detail.spfLookupCount}/10
        </div>
      )}
      {detail.details && (
        <div className="text-xs text-muted-foreground font-mono break-all whitespace-pre-line line-clamp-3">{detail.details}</div>
      )}

      {/* Fix Now button for automated remediation */}
      {onFix && (detail.status === 'fail' || detail.status === 'warning') && !fixState?.success && (
        <div className="pt-1">
          <button
            onClick={(e) => { e.stopPropagation(); onFix(); }}
            disabled={fixState?.loading}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {fixState?.loading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Enabling Enhanced Filtering...
              </>
            ) : (
              <>
                <Wrench className="h-3.5 w-3.5" />
                Fix Now
              </>
            )}
          </button>
        </div>
      )}

      {/* Fix success message */}
      {fixState?.success && (
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-2.5 flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
          <span className="text-xs text-emerald-600 dark:text-emerald-400">{fixState.success}</span>
        </div>
      )}

      {/* Fix error message */}
      {fixState?.error && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-2.5 flex items-center gap-2">
          <XCircle className="h-4 w-4 text-red-500 shrink-0" />
          <span className="text-xs text-red-600 dark:text-red-400">{fixState.error}</span>
        </div>
      )}

      {remediation && (detail.status === 'fail' || detail.status === 'warning') && (
        <div className="pt-1">
          <button
            onClick={(e) => { e.stopPropagation(); setShowRemediation(!showRemediation); }}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
          >
            <Wrench className="h-3.5 w-3.5" />
            How to Fix Manually
            {showRemediation ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
          {showRemediation && (
            <ol className="mt-2 space-y-1.5 text-xs text-muted-foreground list-decimal list-inside">
              {remediation.map((step, i) => (
                <li key={i} className="leading-relaxed">{step}</li>
              ))}
            </ol>
          )}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-muted-foreground" />
            {title}
            <StatusIcon className={`h-5 w-5 ${config.iconClass} ml-auto`} />
          </DialogTitle>

          <div className="space-y-4 mt-2">
            <p className="text-sm text-foreground">{detail.message}</p>

            {detail.spfLookupCount !== undefined && (
              <div className={`flex items-center gap-3 rounded-lg border p-3 ${
                detail.spfLookupCount > 10 ? 'border-red-500/30 bg-red-500/5' :
                detail.spfLookupCount === 10 ? 'border-amber-500/30 bg-amber-500/5' :
                detail.spfLookupCount >= 8 ? 'border-yellow-500/30 bg-yellow-500/5' :
                'border-emerald-500/30 bg-emerald-500/5'
              }`}>
                <div className="flex-1">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">SPF DNS Lookups</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          detail.spfLookupCount > 10 ? 'bg-red-500' :
                          detail.spfLookupCount === 10 ? 'bg-amber-500' :
                          detail.spfLookupCount >= 8 ? 'bg-yellow-500' :
                          'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min((detail.spfLookupCount / 10) * 100, 100)}%` }}
                      />
                    </div>
                    <span className={`text-sm font-bold ${
                      detail.spfLookupCount > 10 ? 'text-red-600 dark:text-red-400' :
                      detail.spfLookupCount === 10 ? 'text-amber-600 dark:text-amber-400' :
                      detail.spfLookupCount >= 8 ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-emerald-600 dark:text-emerald-400'
                    }`}>{detail.spfLookupCount}/10</span>
                  </div>
                </div>
                {detail.spfLookupCount > 10 && (
                  <span className="text-xs text-red-600 dark:text-red-400 font-medium">Over limit — SPF may fail</span>
                )}
                {detail.spfLookupCount === 10 && (
                  <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">At limit — no room for more</span>
                )}
              </div>
            )}

            {(() => {
              // Use structured dnsRecords if present, otherwise try parsing legacy details
              const dnsRecs = (detail.dnsRecords && detail.dnsRecords.length > 0)
                ? detail.dnsRecords
                : (detail.details ? parseLegacyDnsRecords(checkKey, detail.details) : []);

              return dnsRecs.length > 0 ? (
                <>
                  {detail.details && detail.dnsRecords && detail.dnsRecords.length > 0 && (
                    <p className="text-xs text-muted-foreground">{detail.details}</p>
                  )}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-foreground">DNS Records to Create</h4>
                    {dnsRecs.map((rec, i) => (
                      <DnsRecordRow key={i} rec={rec} />
                    ))}
                  </div>
                </>
              ) : detail.details ? (
                <CopyField label="Details" value={detail.details} />
              ) : null;
            })()}

            {/* Step-by-step instructions */}
            {detail.instructions && detail.instructions.length > 0 && (
              <div className="border-t border-border pt-3">
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-1.5">
                  <Wrench className="h-4 w-4" />
                  Steps
                </h4>
                <ol className="space-y-1.5 text-sm text-muted-foreground list-decimal list-inside">
                  {detail.instructions.map((step, i) => (
                    <li key={i} className="leading-relaxed">{step}</li>
                  ))}
                </ol>
              </div>
            )}

            {/* Legacy remediation (for non-DNS checks like MX/Connector/EF) */}
            {!detail.instructions && remediation && (detail.status === 'fail' || detail.status === 'warning') && (
              <div className="border-t border-border pt-3">
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-1.5">
                  <Wrench className="h-4 w-4" />
                  How to Fix
                </h4>
                <ol className="space-y-1.5 text-sm text-muted-foreground list-decimal list-inside">
                  {remediation.map((step, i) => (
                    <li key={i} className="leading-relaxed">{step}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ClientEmailHealth({ clientId }: ClientEmailHealthProps) {
  const { addJob, updateJob } = useJobTracker();
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [latest, setLatest] = useState<SpamHeroCheckResult | null>(null);
  const [history, setHistory] = useState<SpamHeroCheckResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [knownIps, setKnownIps] = useState<string[]>([]);
  const [efFixLoading, setEfFixLoading] = useState(false);
  const [efFixSuccess, setEfFixSuccess] = useState<string | null>(null);
  const [efFixError, setEfFixError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getClientEmailHealth(clientId);
      setLatest(data.latest);
      setHistory(data.history);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email health data');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchData();
    getSpamHeroSettings()
      .then(d => setKnownIps(d.settings.knownGoodIps || []))
      .catch(() => {});
  }, [fetchData]);

  const handleRunCheck = async () => {
    setRunning(true);
    try {
      const result = await runEmailHealthCheck(clientId);
      setLatest(result);
      setHistory(prev => [result, ...prev.slice(0, 9)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Check failed');
    } finally {
      setRunning(false);
    }
  };

  const handleFixEF = async () => {
    if (!latest?.connectorName) return;
    setEfFixLoading(true);
    setEfFixSuccess(null);
    setEfFixError(null);

    const jobId = `ef-fix-${clientId}-${Date.now()}`;
    addJob({
      id: jobId,
      type: 'ef-fix',
      label: 'Fix Enhanced Filtering',
    });

    try {
      const result = await fixEnhancedFiltering(clientId, latest.connectorName, knownIps);
      if (result.success) {
        setEfFixSuccess(result.message || 'Enhanced Filtering enabled successfully');
        updateJob(jobId, { status: 'completed', completedAt: new Date().toISOString() });
        // Auto-refresh the health check to verify the fix
        setTimeout(async () => {
          try {
            setRunning(true);
            const refreshed = await runEmailHealthCheck(clientId);
            setLatest(refreshed);
            setHistory(prev => [refreshed, ...prev.slice(0, 9)]);
          } catch {
            // Refresh failed, but fix was successful
          } finally {
            setRunning(false);
          }
        }, 2000);
      } else {
        const errMsg = result.error || result.details || 'Fix failed';
        setEfFixError(errMsg);
        updateJob(jobId, { status: 'failed', completedAt: new Date().toISOString(), error: errMsg });
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Fix request failed';
      setEfFixError(errMsg);
      updateJob(jobId, { status: 'failed', completedAt: new Date().toISOString(), error: errMsg });
    } finally {
      setEfFixLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-semibold text-foreground">Email Health</h3>
        </div>
        <button
          onClick={handleRunCheck}
          disabled={running}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Run Check
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {!latest ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center space-y-3">
          <Mail className="h-10 w-10 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground">No email health check has been run for this client yet.</p>
          <button
            onClick={handleRunCheck}
            disabled={running}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Run First Check
          </button>
        </div>
      ) : (
        <>
          {/* Overall Status */}
          <OverallBanner status={latest.overallStatus} checks={latest.checks} usesSpamHero={latest.usesSpamHero} />

          {/* Check Cards 2x2 Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CheckCard checkKey="mxRouting" title="MX Routing" icon={Mail} detail={latest.checks.mxRouting} knownIps={knownIps} />
            <CheckCard checkKey="inboundConnector" title="Inbound Connector" icon={Network} detail={latest.checks.inboundConnector} knownIps={knownIps} />
            <CheckCard
              checkKey="enhancedFiltering"
              title="Enhanced Filtering"
              icon={Filter}
              detail={latest.checks.enhancedFiltering}
              knownIps={knownIps}
              onFix={latest.connectorName ? handleFixEF : undefined}
              fixState={{ loading: efFixLoading, success: efFixSuccess, error: efFixError }}
            />
            <CheckCard checkKey="ipMatch" title="IP Match" icon={ShieldCheck} detail={latest.checks.ipMatch} knownIps={knownIps} />
          </div>

          {/* Email Authentication (DNS) — shows for ALL clients */}
          {(latest.checks.spf || latest.checks.dkim || latest.checks.dmarc) && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Email Authentication (DNS)</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {latest.checks.spf && (
                  <CheckCard checkKey="spf" title="SPF Record" icon={Globe} detail={latest.checks.spf} knownIps={knownIps} />
                )}
                {latest.checks.dkim && (
                  <CheckCard checkKey="dkim" title="DKIM" icon={Key} detail={latest.checks.dkim} knownIps={knownIps} />
                )}
                {latest.checks.dmarc && (
                  <CheckCard checkKey="dmarc" title="DMARC" icon={ShieldCheck} detail={latest.checks.dmarc} knownIps={knownIps} />
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            {latest.domain && <span>Domain: <span className="font-mono">{latest.domain}</span></span>}
            {latest.connectorName && <span>Connector: <span className="font-medium">{latest.connectorName}</span></span>}
            <span>Checked: {format(parseISO(latest.checkedAt), 'MMM d, yyyy h:mm a')} ({latest.checkedBy})</span>
          </div>

          {/* Last Scheduled Check Indicator */}
          {(() => {
            const lastScheduled = history.find(h => h.checkedBy === 'scheduled');
            if (lastScheduled) {
              const checkedDate = parseISO(lastScheduled.checkedAt);
              const hoursAgo = Math.round((Date.now() - checkedDate.getTime()) / (1000 * 60 * 60));
              const timeLabel = hoursAgo < 1 ? 'less than 1 hour ago'
                : hoursAgo === 1 ? '1 hour ago'
                : hoursAgo < 24 ? `${hoursAgo} hours ago`
                : `${Math.round(hoursAgo / 24)} day${Math.round(hoursAgo / 24) === 1 ? '' : 's'} ago`;
              return (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  <span>Last automated check: <span className="font-medium text-foreground">{timeLabel}</span> ({format(checkedDate, 'MMM d, h:mm a')})</span>
                </div>
              );
            }
            return (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>No automated checks recorded. Configure in Email Health Dashboard.</span>
              </div>
            );
          })()}

          {/* History */}
          {history.length > 1 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <h4 className="font-semibold text-foreground text-sm">Change History</h4>
                <span className="text-xs text-muted-foreground">Only recorded when results change</span>
              </div>
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">Date</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">Status</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">MX</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">Connector</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">EF</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">IPs</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">SPF</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">DKIM</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">DMARC</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">Triggered By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {history.map(h => {
                      const StatusIcon = h.overallStatus === 'healthy' ? CheckCircle
                        : h.overallStatus === 'critical' ? XCircle
                        : h.overallStatus === 'issues' ? AlertTriangle
                        : MinusCircle;
                      const statusColor = h.overallStatus === 'healthy' ? 'text-emerald-500'
                        : h.overallStatus === 'critical' ? 'text-red-500'
                        : h.overallStatus === 'issues' ? 'text-amber-500'
                        : 'text-muted-foreground';

                      const checkIcon = (status: string) => {
                        if (status === 'pass') return <CheckCircle className="h-3.5 w-3.5 text-emerald-500 mx-auto" />;
                        if (status === 'fail') return <XCircle className="h-3.5 w-3.5 text-red-500 mx-auto" />;
                        if (status === 'warning') return <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mx-auto" />;
                        return <MinusCircle className="h-3.5 w-3.5 text-muted-foreground mx-auto" />;
                      };

                      return (
                        <tr key={h.id} className="hover:bg-muted/30">
                          <td className="px-4 py-2 text-xs text-muted-foreground">
                            {format(parseISO(h.checkedAt), 'MMM d, h:mm a')}
                          </td>
                          <td className="px-4 py-2 text-center"><StatusIcon className={`h-4 w-4 ${statusColor} mx-auto`} /></td>
                          <td className="px-4 py-2 text-center">{checkIcon(h.checks.mxRouting.status)}</td>
                          <td className="px-4 py-2 text-center">{checkIcon(h.checks.inboundConnector.status)}</td>
                          <td className="px-4 py-2 text-center">{checkIcon(h.checks.enhancedFiltering.status)}</td>
                          <td className="px-4 py-2 text-center">{checkIcon(h.checks.ipMatch.status)}</td>
                          <td className="px-4 py-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {h.checks.spf ? checkIcon(h.checks.spf.status) : <MinusCircle className="h-3.5 w-3.5 text-muted-foreground" />}
                              {h.checks.spf?.spfLookupCount !== undefined && (
                                <span className={`text-[10px] font-mono font-bold ${
                                  h.checks.spf.spfLookupCount > 10 ? 'text-red-500' :
                                  h.checks.spf.spfLookupCount === 10 ? 'text-amber-500' :
                                  h.checks.spf.spfLookupCount >= 8 ? 'text-yellow-500' :
                                  'text-emerald-500'
                                }`}>{h.checks.spf.spfLookupCount}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2 text-center">{h.checks.dkim ? checkIcon(h.checks.dkim.status) : <MinusCircle className="h-3.5 w-3.5 text-muted-foreground mx-auto" />}</td>
                          <td className="px-4 py-2 text-center">{h.checks.dmarc ? checkIcon(h.checks.dmarc.status) : <MinusCircle className="h-3.5 w-3.5 text-muted-foreground mx-auto" />}</td>
                          <td className="px-4 py-2 text-xs text-muted-foreground capitalize">{h.checkedBy}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
